import {
    DriveFolderUpload,
    UploadFile
} from '@mui/icons-material';
import {
    Menu,
    MenuItem,
    Typography
} from '@mui/material';
import { MenuProps } from '@mui/material/Menu';
import { nanoid } from '@reduxjs/toolkit';
import { useModal } from 'hooks/useModal';
import { ProgressItemStatuses } from 'interfaces/progress';
import React, {
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { createCategoryNested } from 'slices/categoriesActions';
import { doesDocumentExist, uploadDocument, refreshDocumentTreeLevels } from 'slices/documentsActions';
import { addItem as addProgressItem } from 'slices/progressSlice';
import { addSnackbar } from 'slices/snackbarsSlice';
import { allowedExtensions, ROOT_FOLDER_ID } from 'utilities/constants';
import { useAppDispatch } from 'utilities/hooks';

/**
 * Allow to render input attributes
 */
declare module 'react' {
    interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
        directory?: string;
        webkitdirectory?: string;
    }
}

interface AssociativeArrayString {
    [key: string]: string
}

interface UploadMenuProps extends MenuProps {
    uploadref: any;
}

export interface UploadMenuRef {
    drop: (dropFiles: FileList | null) => void;
};

const UploadMenu = (props: UploadMenuProps & {
    onComplete?: (values: any[]) => void,
}) => {
    const [files, setFiles] = useState<FileList | null>(null);
    const [filesData, setFilesData] = useState<Record<string, any>>([]);
    const [isFolder, setIsFolder] = useState<boolean>(false);
    const { onClose, onComplete, children, ...uploadMenuProps } = props;
    const dispatch = useAppDispatch();
    const { categoryId } = useParams();
    const { openModal } = useModal();

    const filesInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isFolder) {
            checkFoldersAndUpload();
        } else {
            checkFilesAndUpload();
        }
    }, [files]);

    const drop = (_files: FileList | null) => {
        Array.from(_files || []).some(element => {
            let path = Object(element).path;
            // On some environments, the path is prefixed with './' instead of '/'
            if (path.startsWith('./')) {
                path = path.replace('./', '');
            }
            let isFolder = path.replace(element.name, '').split('/').filter((x: string) => x).length > 0;
            setIsFolder(isFolder);
        });
        if (_files) {
            setFiles(_files);
        }
    };

    useImperativeHandle(props.uploadref, () => ({
        drop: (dropFiles: FileList | null) => drop(dropFiles),
    }));

    const handleFilesClick = (event: React.MouseEvent<HTMLElement>) => {
        filesInputRef.current?.click();
        setIsFolder(false);
        setFilesData([]);

        if (onClose) {
            onClose(event, "backdropClick");
        }
    };

    const handleFolderClick = (event: React.MouseEvent<HTMLElement>) => {
        folderInputRef.current?.click();
        setIsFolder(true);
        setFilesData([]);

        if (onClose) {
            onClose(event, "backdropClick");
        }
    };

    const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const _files: FileList | null = event.target.files;

        if (_files) {
            setFiles(_files);
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const target = event.target as HTMLInputElement;
        target.value = '';
    };

    const checkFilesAndUpload = () => {
        if (!files) return;

        let _filesData: Record<string, any> = [];
        let fileIdsInCheckOrder: string[] = [];
        let promises: any[] = [];

        for (var i = 0; i < files.length; i++) {
            let file = files[i];

            const fileId: string = nanoid();
            const fileExtension = file.name.split('.').pop();

            if (fileExtension) {
                if (!allowedExtensions.includes('.' + fileExtension.toLowerCase())) {
                    dispatch(addSnackbar({
                        message: `File '${file.name}' extension is not supported.`,
                        type: 'error'
                    }));
                    continue;
                }
            }

            const data = {
                id: fileId,
                name: file.name,
                extension: fileExtension ? fileExtension.toLowerCase() : null,
                category_id: categoryId ? categoryId : null,
            };

            promises.push(dispatch(doesDocumentExist(data)));
            fileIdsInCheckOrder.push(fileId);

            _filesData[fileId] = Object.assign({
                file: file,
            }, data);
        }
        setFilesData(_filesData);

        Promise.all(promises).then((responses) => {
            let hasConflict = false;
            let access = null;
            for (var i = 0; i < responses.length; i++) {
                const response = responses[i];
                const fileId = fileIdsInCheckOrder[i];
                if (response.meta?.requestStatus === 'rejected') {
                    delete _filesData[fileId];
                    dispatch(addSnackbar({
                        message: response.payload?.data?.message || 'Failed to validate file name before upload.',
                        type: 'error',
                    }));
                    continue;
                }
                if (!response.payload?.data) continue;

                hasConflict = true;
                if (response.payload.data.access) {
                    access = response.payload.data.access;
                }
            }

            if (!Object.keys(_filesData).length) {
                return;
            }

            if (hasConflict) {
                openModal('resolveUploadModal', {
                    access,
                }, {
                    onResolve: (forceRename: boolean) => {
                        upload(_filesData, forceRename);
                    },
                });
            } else {
                upload(_filesData);
            }
        }).catch(() => { });
    };

    const checkFoldersAndUpload = () => {
        if (!files) return;

        let excludedFiles: string[] = [];
        let _filesData: Record<string, any> = [];
        let relativePaths: AssociativeArrayString = {};
        for (var i = 0; i < files.length; i++) {
            let file = files[i];
            const fileId: string = nanoid();
            const fileExtension = file.name.split('.').pop();

            let relativePath = file['webkitRelativePath'] || (file as any)['path'];
            relativePath = relativePath.substring(0, relativePath.lastIndexOf('/'));
            if (relativePath.startsWith('/')) relativePath = relativePath.substring(1);
            relativePaths[fileId] = relativePath;

            if (fileExtension) {
                if (!allowedExtensions.includes('.' + fileExtension.toLowerCase())) {
                    excludedFiles.push(relativePath + '/' + file.name);
                    continue;
                }
            }

            _filesData[fileId] = {
                id: fileId,
                file: file,
                path: relativePath,
                name: file.name,
                extension: fileExtension ? fileExtension.toLowerCase() : null,
                category_id: categoryId ? categoryId : null,
            };
        }

        const triggerUpload = () => {
            setFilesData(_filesData);

            dispatch(createCategoryNested({
                parent_id: categoryId ? categoryId : null,
                paths: relativePaths,
            }))
                .unwrap()
                .then((response) => {
                    const categoriesMap: AssociativeArrayString = response.data;
                    for (const _fileId in _filesData) {
                        _filesData[_fileId]['category_id'] = categoriesMap[_fileId];
                    }
                    setFilesData(_filesData);
                    // Show the new folders now; upload() refreshes again once the
                    // files have landed in them and changed their counts.
                    dispatch(refreshDocumentTreeLevels([Number(categoryId ?? ROOT_FOLDER_ID)]));
                    upload(_filesData);
                }).catch(() => { });
        }

        let text = 'Note: If there are any files which already exist, they will be uploaded as a new version.';
        if (excludedFiles.length > 0) {
            const MAX_DISPLAY = 10;
            const filesToShow = excludedFiles.slice(0, MAX_DISPLAY);
            const fileList = filesToShow.map(filename => `- ${filename.replaceAll('/', ' > ')}`).join('\n');

            text += '\n\nThe following files are not supported and will not be uploaded. Are you sure want to continue?\n' + fileList;

            if (excludedFiles.length > MAX_DISPLAY) {
                text += '\n (and ' + (excludedFiles.length - MAX_DISPLAY) + ' more)'
            }
        }

        openModal('confirmationModal', {
            title: 'Upload Folder',
            text: text,
        }, {
            onConfirm: () => {
                triggerUpload();
            }
        });
    };

    const upload = (filesData: Record<string, any>, forceRename: Boolean = false) => {
        let promises: any[] = [];

        for (const fileId in filesData) {
            const fileData = filesData[fileId];
            const formData = new FormData();
            formData.append('file', fileData.file);
            formData.append('file_id', fileData.id);
            formData.append('file_props', JSON.stringify({
                category_id: fileData.category_id,
                force_rename: forceRename,
            }));

            dispatch(addProgressItem({
                id: fileData.id,
                data: {
                    name: fileData.name,
                    path: fileData.path,
                    size: fileData.file.size,
                    type: fileData.file.type,
                    lastModified: fileData.file.lastModified,
                },
                status: ProgressItemStatuses.PROCESSING,
            }));

            promises.push(dispatch(uploadDocument(formData)).catch((error) => error));
        }

        Promise.all(promises).then((values) => {
            dispatch(refreshDocumentTreeLevels(
                Object.values(filesData).map((fileData: any) => fileData.category_id),
            ));

            if (onComplete) {
                onComplete(values);
            }
        }).catch(() => { });
    };

    return <>
        <Menu
            elevation={1}
            keepMounted
            onClose={onClose}
            {...uploadMenuProps}
        >
            <MenuItem onClick={handleFilesClick} sx={{ minWidth: 200 }}>
                <UploadFile />
                <Typography ml={2}>Files</Typography>
                <input
                    type="file"
                    ref={filesInputRef}
                    onChange={handleFilesChange}
                    onClick={handleClick}
                    accept={allowedExtensions.join(',')}
                    multiple
                    hidden
                />
            </MenuItem>
            <MenuItem onClick={handleFolderClick}>
                <DriveFolderUpload />
                <Typography ml={2}>Folder</Typography>
                <input
                    type="file"
                    ref={folderInputRef}
                    onChange={handleFilesChange}
                    onClick={handleClick}
                    accept={allowedExtensions.join(',')}
                    webkitdirectory="true"
                    directory="true"
                    hidden
                />
            </MenuItem>
            {children}
        </Menu>
    </>;
};

export default UploadMenu;